import React from "react";
import { StyleSheet, View } from "react-native";
import { useDispatch } from "react-redux";
import { Button, Paragraph, useTheme } from "react-native-paper";
import { Block } from "@block";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { testProps } from "app/shared/util/test-props";
import { InputText } from "app/shared/components/form/input";
import { RegisterOptions, useForm } from "react-hook-form";
import { Countdown } from "app/shared/components/countdown/countdown";
import { ChangePasswordState, FormChangePasswordData } from "./reducer/types";
import QRCode from "react-native-qrcode-svg";

enum FormEnum {
  otpCode,
}

interface FormOTPProps {
  challengeValue?: string;
  errorEndOTP?: boolean;
  request: Function;
  cancel: Function;
  changePassStore: ChangePasswordState;
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

function OTPChangePassword(props: FormOTPProps) {
  const { challengeValue, errorEndOTP = false, cancel, request, changePassStore } = props;
  const dispatchStore = useDispatch();
  // setup error state for displaying error messages
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();

  const [endTimer, setEndTimer] = React.useState(false);

  const { control, handleSubmit, clearErrors, trigger } = useForm<FormChangePasswordData>({
    defaultValues: {
      otpCode: "",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    otpCode: {
      required: {
        value: true,
        message: t("COMMON_OTP_CHUA_NHAP"),
      },
      minLength: {
        value: 6,
        message: t("COMMON_OTP_KHONG_HOP_LE"),
      },
    },
  };

  const textInputOTP: object = {
    maxLength: 6,
    disabled: endTimer,
    autoFocus: true,
  };

  React.useEffect(() => {}, [dispatchStore]);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textInput: {
          color: colors.accent,
          backgroundColor: colors.surface,
          fontWeight: "700",
          textShadowColor: "black",
          fontSize: 12,
          flex: 1,
        },
        buttonForm: { width: 128, alignSelf: "center", backgroundColor: colors.backgroundForm },
        buttonText: { ...fonts.medium },
        textField: { ...fonts.medium, color: colors.text, fontSize: 13, textAlign: "center", paddingVertical: spacing.small },
        mobileShowStyles: {
          display: challengeValue?.length ? "flex" : "none",
        },
        qrCodeDescription: { ...fonts.regular, color: colors.text, fontSize: 12, textAlign: "center" },
        challengeLabel: { ...fonts.regular, color: colors.text, fontSize: 12, textAlign: "center" },
        challengeValue: { ...fonts.medium, fontSize: 13 },
        textError: {
          ...fonts.regular,
          color: colors.error,
          fontSize: 11,
          lineHeight: 16,
          textAlign: "center",
          paddingTop: spacing.small,
          display: changePassStore.statusAuth === "error" ? "flex" : "none",

        },
      }),
    [challengeValue, fonts, colors, spacing, changePassStore],
  );

  const onBlurInput = () => {
    trigger("otpCode");
  };

  const handleRequestChangePassword = (value: FormChangePasswordData): void => {
    const data = {
      otpCode: value.otpCode,
    };
    request(data);
  };

  const onPressCancel = () => {
    setEndTimer(false);
    cancel();
  };

  const onPressAccept = () => {
    handleSubmit(handleRequestChangePassword).call(() => {});
  };

  const onCompleteCountDown = () => {
    setEndTimer(true);
    clearErrors("otpCode");
  };

  /**
   * MARK: - Navigation
   */

  const QRContent = (
    <View style={styles.mobileShowStyles}>
      <Block center middle flex={1}>
        <StackSpacing size={"small"} />
        <Paragraph style={styles.qrCodeDescription}>{t("COMMON_QUET_LAY_MA_XA_THUC")}</Paragraph>
        <StackSpacing size={"small"} />
        {challengeValue?.length ? (
          <View {...testProps("qrCodeMobile")}>
            <QRCode value={challengeValue} size={120} color={colors.backdrop} />
          </View>
        ) : (
          <Block />
        )}
        <StackSpacing size={"small"} />
        <Paragraph style={styles.challengeLabel}>{t("SM_OTP_MA_GIAO_DICH")}: <Paragraph style={styles.challengeValue}>{challengeValue}</Paragraph></Paragraph>
      </Block>
    </View>
  );

  const FormContent = (
    <Block ph={spacing.medium} pt={spacing.small}>
      {QRContent}
      <Paragraph style={styles.textField}>{t("SM_OTP_MA_OTP_TIEU_DE")}</Paragraph>
      <Block flex={1} ph={spacing.medium}>
        <InputText
          name="otpCode"
          control={control}
          rules={validateSchemeForm.otpCode}
          inputProps={{ ...textInputOTP, style: styles.textInput, editable: !endTimer }}
          label={t("COMMON_NHAP_MA_XA_THUC")}
          onBlur={() => onBlurInput()}
          loading={changePassStore.statusAuth === "pending" ? true : false}
        />
        <Paragraph style={styles.textError}>{changePassStore.errorMsg}</Paragraph>
      </Block>
      <Countdown onComplete={onCompleteCountDown} errorEndOTP={errorEndOTP} />
    </Block>
  );

  const ButtonRow = (
    <Block row middle pv={spacing.large}>
      <Block w={0} />
      <Button onPress={onPressCancel} mode="contained" style={styles.buttonForm}>
        {t("COMMON_QUAY_LAI")}
      </Button>
      <QueueSpacing size="large" />
      <Button
        onPress={onPressAccept}
        mode="contained"
        {...testProps("nextBtn")}
        disabled={endTimer}
        style={{ opacity: endTimer ? 0.5 : 1 }}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  return (
    <Block br={12}>
      {FormContent}
      {ButtonRow}
    </Block>
  );
}

export default OTPChangePassword;
